import React, { useState } from 'react';
import './PDFWorkflowDemo.css';

/**
 * PDF Workflow Demonstration Page
 * Shows how PDF template system would work
 */
function PDFWorkflowDemo() {
  const [currentStep, setCurrentStep] = useState(1);

  const steps = [
    {
      number: 1,
      title: "📥 Step 1: Download PDF Template",
      description: "Worker downloads blank PDF form from Documents page",
      details: [
        "✓ PDF template uploaded by admin in Manage Documents",
        "✓ Worker goes to Documents page (no login needed)",
        "✓ Downloads 'Concrete Safety Daily Log.pdf'",
        "✓ All underlines, formatting, borders preserved perfectly"
      ],
      screenshot: "download-template.png"
    },
    {
      number: 2,
      title: "✍️ Step 2: Fill PDF Form",
      description: "Worker fills the PDF using any PDF reader",
      details: [
        "✓ Open PDF in Adobe Reader (free)",
        "✓ OR use Foxit Reader (free)",
        "✓ OR fill in browser (Chrome, Edge)",
        "✓ Fill Client, Location, CSM name, Time, etc.",
        "✓ Save filled PDF to computer"
      ],
      tools: [
        { name: "Adobe Acrobat Reader", free: true, link: "get.adobe.com/reader" },
        { name: "Foxit Reader", free: true, link: "foxit.com/downloads" },
        { name: "Browser (Chrome/Edge)", free: true, link: "Built-in" }
      ],
      screenshot: "fill-pdf.png"
    },
    {
      number: 3,
      title: "📤 Step 3: Upload Filled PDF",
      description: "Worker logs in and uploads completed PDF",
      details: [
        "✓ Login to worker portal",
        "✓ Go to Upload Filled Form page",
        "✓ Fill: Name, Job Site, Date, Hours",
        "✓ Upload the saved PDF file",
        "✓ Submit"
      ],
      screenshot: "upload-filled.png"
    },
    {
      number: 4,
      title: "👁️ Step 4: Admin Views/Prints",
      description: "Admin can view perfect PDF instantly",
      details: [
        "✓ Admin sees submission in dashboard",
        "✓ Click to view details",
        "✓ PDF displays with perfect formatting",
        "✓ All underlines, borders preserved",
        "✓ Click to open in new tab",
        "✓ Print directly (Ctrl + P)",
        "✓ No conversion needed!"
      ],
      screenshot: "admin-view.png"
    }
  ];

  return (
    <div className="pdf-demo-container">
      <div className="demo-header">
        <h1>📄 PDF Template Workflow - Live Demo</h1>
        <p>See how the PDF template system works (better formatting preservation)</p>
      </div>

      {/* Step Navigation */}
      <div className="step-navigator">
        {steps.map((step) => (
          <button
            key={step.number}
            className={`step-btn ${currentStep === step.number ? 'active' : ''} ${currentStep > step.number ? 'completed' : ''}`}
            onClick={() => setCurrentStep(step.number)}
          >
            <div className="step-number">{step.number}</div>
            <div className="step-name">Step {step.number}</div>
          </button>
        ))}
      </div>

      {/* Current Step Display */}
      <div className="step-content">
        {steps.map((step) => (
          currentStep === step.number && (
            <div key={step.number} className="step-detail">
              <h2>{step.title}</h2>
              <p className="step-description">{step.description}</p>

              <div className="step-details-box">
                <h3>How it works:</h3>
                <ul>
                  {step.details.map((detail, idx) => (
                    <li key={idx}>{detail}</li>
                  ))}
                </ul>
              </div>

              {/* Tools section for Step 2 */}
              {step.tools && (
                <div className="tools-box">
                  <h3>🛠️ Free Tools to Fill PDF:</h3>
                  <div className="tools-grid">
                    {step.tools.map((tool, idx) => (
                      <div key={idx} className="tool-card">
                        <div className="tool-name">{tool.name}</div>
                        <div className="tool-badge">{tool.free ? '✓ FREE' : ''}</div>
                        <div className="tool-link">{tool.link}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Screenshot placeholder */}
              <div className="screenshot-box">
                <div className="screenshot-placeholder">
                  <span>📸</span>
                  <p>Screenshot: {step.screenshot}</p>
                  <small>(Actual interface screenshot would appear here)</small>
                </div>
              </div>
            </div>
          )
        ))}
      </div>

      {/* Navigation Buttons */}
      <div className="demo-navigation">
        <button
          className="nav-btn prev"
          onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
          disabled={currentStep === 1}
        >
          ← Previous
        </button>
        <button
          className="nav-btn next"
          onClick={() => setCurrentStep(Math.min(4, currentStep + 1))}
          disabled={currentStep === 4}
        >
          Next →
        </button>
      </div>

      {/* Comparison Section */}
      <div className="comparison-section">
        <h2>📊 Comparison: Current vs PDF Template System</h2>
        <div className="comparison-grid">
          <div className="comparison-card current">
            <h3>Current System (Word → PDF Conversion)</h3>
            <ul>
              <li className="pro">✓ Easy for workers (use Word/Excel)</li>
              <li className="pro">✓ Familiar tools</li>
              <li className="con">✗ Underlines may disappear in PDF</li>
              <li className="con">✗ Formatting not 100% preserved</li>
              <li className="con">✗ Conversion takes time</li>
              <li className="con">✗ Requires LibreOffice installed</li>
            </ul>
          </div>

          <div className="comparison-card pdf-template">
            <h3>PDF Template System (Recommended)</h3>
            <ul>
              <li className="pro">✓ Perfect formatting preservation</li>
              <li className="pro">✓ All underlines stay intact</li>
              <li className="pro">✓ No conversion needed</li>
              <li className="pro">✓ Instant view/print</li>
              <li className="pro">✓ No special software needed</li>
              <li className="pro">✓ Browser can fill PDFs (Chrome/Edge)</li>
              <li className="neutral">≈ Workers need PDF reader (free)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Implementation Section */}
      <div className="implementation-section">
        <h2>🚀 How to Implement PDF Template System</h2>
        <div className="implementation-steps">
          <div className="impl-step">
            <div className="impl-number">1</div>
            <div className="impl-content">
              <h4>Convert Word Templates to PDF</h4>
              <p>Open your Word forms → File → Save As → PDF format</p>
              <p className="impl-note">This preserves all underlines and formatting</p>
            </div>
          </div>

          <div className="impl-step">
            <div className="impl-number">2</div>
            <div className="impl-content">
              <h4>Upload PDF Templates</h4>
              <p>Admin → Manage Documents → Upload PDF templates</p>
              <p className="impl-note">Workers can download from Documents page</p>
            </div>
          </div>

          <div className="impl-step">
            <div className="impl-number">3</div>
            <div className="impl-content">
              <h4>Train Workers (Optional)</h4>
              <p>Show workers how to fill PDFs using free Adobe Reader</p>
              <p className="impl-note">Very simple: Download → Fill → Save → Upload</p>
            </div>
          </div>

          <div className="impl-step">
            <div className="impl-number">4</div>
            <div className="impl-content">
              <h4>No Code Changes Needed!</h4>
              <p>Current system already supports PDF upload and viewing</p>
              <p className="impl-note">Just switch from .docx to .pdf templates</p>
            </div>
          </div>
        </div>
      </div>

      {/* Decision Helper */}
      <div className="decision-box">
        <h3>💭 Which System Should You Use?</h3>
        <div className="decision-options">
          <div className="decision-option">
            <h4>Use PDF Templates if:</h4>
            <ul>
              <li>✓ Formatting must be perfect (underlines, borders)</li>
              <li>✓ Forms are professional/legal documents</li>
              <li>✓ You want best quality</li>
              <li>✓ Workers can use Adobe Reader (free)</li>
            </ul>
            <button className="decision-btn recommended">✓ Recommended</button>
          </div>

          <div className="decision-option">
            <h4>Keep Current System if:</h4>
            <ul>
              <li>≈ Workers strongly prefer Word/Excel</li>
              <li>≈ Formatting differences acceptable</li>
              <li>≈ Quick view more important than perfect format</li>
              <li>≈ Original file available for download anyway</li>
            </ul>
            <button className="decision-btn alternative">Works, but not ideal</button>
          </div>
        </div>
      </div>

      <div className="demo-footer">
        <p>This is a demonstration page. Actual implementation requires no code changes!</p>
        <p><strong>Just convert your Word templates to PDF and upload them.</strong></p>
      </div>
    </div>
  );
}

export default PDFWorkflowDemo;
